import React, { useMemo, useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';

import ButtonItem from '../components/ButtonItem';
import GenericAutoCompleteInput from '../components/GenericAutoCompleteInput';
import { loadTossPayments } from '@tosspayments/tosspayments-sdk';

export default function AlbumApplicationPage() {
  const navigate = useNavigate();
  const { me } = useSelector(authSelector.selectAuthState);
  const [album, setAlbum] = useState({ id: null, name: '' });
  const [albumLoading, setAlbumLoading] = useState(false);
  const [albumArtist, setAlbumArtist] = useState('');
  const [albumClient, setAlbumClient] = useState('');
  const [albumReleaseDate, setAlbumReleaseDate] = useState('');
  const [albumType, setAlbumType] = useState('');
  const [physicalAlbumYn, setPhysicalAlbumYn] = useState('N');
  const [riakId, setRiakId] = useState('');
  const [riakProducerName, setRiakProducerName] = useState('');

  const [songs, setSongs] = useState([]);
  const [songInputs, setSongInputs] = useState({});
  const [lyricsModalOpen, setLyricsModalOpen] = useState(false);
  const [currentSongId, setCurrentSongId] = useState(null);
  const [tempLyrics, setTempLyrics] = useState('');
  const [tempLyricsTranslation, setTempLyricsTranslation] = useState('');
  const [hasTranslation, setHasTranslation] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [payments, setPayments] = useState(null);

  useEffect(() => {
    if (!lyricsModalOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') closeLyricsModal();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lyricsModalOpen]);

  const fetchAlbumOptions = async () => {
    const res = await axios.get(`/album?limit=9999`);
    return res.data.items.map((a) => ({ id: Number(a.id), name: a.nameKr }));
  };

  const handleAlbumChange = async (selectedAlbum) => {
    setAlbum(selectedAlbum);
    setAlbumArtist('');
    setAlbumClient('');
    setAlbumReleaseDate('');
    setAlbumType('');
    setSongs([]);
    setSongInputs({});
    setRiakId('');
    setRiakProducerName('');
    setPhysicalAlbumYn('N');

    if (!selectedAlbum?.id) return;

    setAlbumLoading(true);
    try {
      const a = await axios.get(`/album/${selectedAlbum.id}`);
      setAlbumArtist(a.data?.artist?.nameKr);
      setAlbumClient(a.data?.clientDisplay);
      setAlbumReleaseDate(a.data?.releaseDateTime);
      setAlbumType(a.data?.albumTypeId);

      const s = await axios.get(`/album/${selectedAlbum.id}/songs`);
      const sItems = s.data.items;
      sItems.sort((a, b) => {
        const d1 = Number(a.disc || 1),
          d2 = Number(b.disc || 1);
        if (d1 !== d2) return d1 - d2;
        const t1 = Number(a.track || 0),
          t2 = Number(b.track || 0);
        return t1 - t2;
      });
      setSongs(sItems);

      const initInputs = {};
      sItems.forEach((s) => {
        initInputs[s.id] = {
          featuring: '',
          featuringLatestRelease: '',
          performance: '',
          woneumYn: false,
          lyrics: '',
          lyricsTranslation: '',
        };
      });
      setSongInputs(initInputs);
    } catch (e) {
      console.error(e);
    } finally {
      setAlbumLoading(false);
    }
  };

  const updateSongInput = (songId, field, value) => {
    setSongInputs((prev) => ({
      ...prev,
      [songId]: { ...(prev[songId] || {}), [field]: value },
    }));
  };

  const updatewoneumYn = (songId, checked) => {
    const selectedCount = Object.values(songInputs).filter(
      (si) => si.woneumYn
    ).length;
    if (checked && selectedCount >= 3) {
      alert('원음방송은 최대 3곡까지 선택할 수 있습니다.');
      return;
    }
    updateSongInput(songId, 'woneumYn', checked);
  };

  const openLyricsModal = (songId) => {
    setCurrentSongId(songId);
    setTempLyrics(songInputs[songId]?.lyrics || '');
    const trans = songInputs[songId]?.lyricsTranslation || '';
    setTempLyricsTranslation(trans);
    setHasTranslation(!!trans);
    setLyricsModalOpen(true);
  };
  const closeLyricsModal = () => {
    setLyricsModalOpen(false);
    setCurrentSongId(null);
    setTempLyrics('');
    setTempLyricsTranslation('');
    setHasTranslation(false);
    setTranslating(false);
  };
  const autoTranslate = async () => {
    if (!tempLyrics.trim()) return;
    setHasTranslation(true);
    setTranslating(true);
    try {
      const res = await axios.post('/api/public/reviewApply/translate-lyrics', { lyrics: tempLyrics });
      setTempLyricsTranslation(res.data.translation || '');
    } catch (e) {
      alert('번역 중 오류가 발생했습니다.');
    } finally {
      setTranslating(false);
    }
  };
  const saveLyricsModal = () => {
    if (currentSongId) {
      updateSongInput(currentSongId, 'lyrics', tempLyrics);
      updateSongInput(currentSongId, 'lyricsTranslation', hasTranslation ? tempLyricsTranslation : '');
    }
    closeLyricsModal();
  };

  const canSubmit = useMemo(() => {
    if (!album?.id || !songs.length) return false;
    for (const s of songs) {
      if (!songInputs[s.id]?.performance?.trim()) return false;
      if (
        !(s.nameKr || '').includes('Inst.') &&
        !songInputs[s.id]?.lyrics?.trim()
      )
        return false;
    }
    const bcCount = Object.values(songInputs).filter(
      (si) => si.woneumYn
    ).length;
    return bcCount > 0 && bcCount <= 3;
  }, [album, songs, songInputs]);

  const onSubmit = async () => {
    if (!canSubmit) {
      alert('필수 항목을 확인해 주세요.');
      return;
    }

    const orderId = `order_${Date.now()}`;
    const amount = 110000;

    const payload = {
      clientId: me?.clientId,
      albumId: album.id,
      physicalAlbumYn,
      riakId: riakId.trim(),
      riakProducerName: riakProducerName.trim(),
      songs: songs.map((s) => ({
        songId: s.id,
        featuring: (songInputs[s.id]?.featuring || '').trim(),
        featuringLatestRelease: (
          songInputs[s.id]?.featuringLatestRelease || ''
        ).trim(),
        performance: (songInputs[s.id]?.performance || '').trim(),
        woneumYn: songInputs[s.id]?.woneumYn || false,
        lyrics: (songInputs[s.id]?.lyrics || '').trim(),
        lyricsTranslation: (songInputs[s.id]?.lyricsTranslation || '').trim() || null,
      })),
    };

    sessionStorage.setItem('reviewApplyPayload', JSON.stringify(payload));

    try {
      await payments.requestPayment({
        method: 'CARD',
        amount: { currency: 'KRW', value: amount },
        orderId,
        orderName: 'p!nk 방송심의 서비스',
        successUrl: `${window.location.origin}/payment/success?orderId=${orderId}&amount=${amount}`,
        failUrl: `${window.location.origin}/payment/fail`,
      });
    } catch (e) {
      console.error(e);
      alert('결제창 열기 중 오류가 발생했습니다.');
    }
  };

  useEffect(() => {
    async function fetchPaymentWidget() {
      const tossPayments = await loadTossPayments(
        'live_ck_DpexMgkW36RDW5Qab2WB3GbR5ozO'
      );
      const customerKey = `client_${me.id}`;
      const widget = tossPayments.payment({ customerKey });
      setPayments(widget);
    }
    fetchPaymentWidget();
  }, [me?.id]);

  return (
    <>
      {/* 안내 텍스트 */}
      <div className="mb-8 text-center">
          <div className="text-xl sm:text-2xl font-bold mb-3 text-white tracking-tight">
            신청서 작성
          </div>
          <div className="text-sm text-slate-400">
            각 방송사의 사정에 따라 처리기간이 다르므로{' '}
            <span className="text-cyan-600 font-bold">최소한 발매 15일 전</span>
            에 신청해야 발매 전 심의가 완료될 수 있습니다.
            <br /> 본 양식에 기입된 내용을 임의로 수정하여 접수하지 않으니,
            신중히 기입 부탁드립니다.
          </div>
        </div>

        <div className="space-y-6 max-w-3xl mx-auto">
          {/* 앨범 선택 */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
              앨범명 <span className="text-red-500">*</span>
            </label>
            <GenericAutoCompleteInput
              value={album}
              onChange={handleAlbumChange}
              fetchOptions={fetchAlbumOptions}
              placeholder="앨범을 선택하세요"
            />
          </div>

          {/* 앨범 정보 */}
          <div className="grid grid-cols-1 gap-4">
            {me?.adminYn === 'Y' && (
              <ReadOnlyField
                label="권리사명"
                value={albumClient}
                loading={albumLoading}
              />
            )}
            <ReadOnlyField
              label="아티스트명"
              value={albumArtist}
              loading={albumLoading}
            />
            <ReadOnlyField
              label="발매일"
              value={albumReleaseDate}
              loading={albumLoading}
            />
            <ReadOnlyField
              label="앨범 타입"
              value={
                albumType === 1
                  ? '싱글'
                  : albumType === 2
                  ? 'EP'
                  : albumType === 3
                  ? '정규'
                  : albumType === 4
                  ? '컴필레이션'
                  : ''
              }
              loading={albumLoading}
            />
          </div>

          {/* 앨범 추가 입력 */}
          <div>
            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">
              앨범 추가 정보
            </div>
            <div className="border border-white/[0.08] rounded-2xl p-4 space-y-4 bg-[#182334]">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                  피지컬앨범 유무
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <input
                      type="radio"
                      name="physical"
                      value="Y"
                      checked={physicalAlbumYn === 'Y'}
                      onChange={(e) => setPhysicalAlbumYn(e.target.value)}
                    />
                    있음
                  </label>
                  <label className="flex items-center gap-2 text-sm text-slate-200">
                    <input
                      type="radio"
                      name="physical"
                      value="N"
                      checked={physicalAlbumYn === 'N'}
                      onChange={(e) => setPhysicalAlbumYn(e.target.value)}
                    />
                    없음
                  </label>
                </div>

                {physicalAlbumYn === 'Y' && (
                  <div className="text-sm text-rose-400 mt-2">
                    피지컬앨범이 있는 경우, 포장이 되어 있고 판매 인지가
                    붙어있는 <b>유통 음반 27장</b>을 보내주셔야 합니다.
                    <br />※ 소비자가 구매 시 받는 상태와 동일한 앨범
                  </div>
                )}
              </div>

              <InputField
                label="RIAK 아이디"
                value={riakId}
                onChange={(e) => setRiakId(e.target.value)}
                placeholder="예) pink"
              />
              <InputField
                label="RIAK 제작사명"
                value={riakProducerName}
                onChange={(e) => setRiakProducerName(e.target.value)}
                placeholder="예) 핑크페이퍼"
              />

              <div className="text-sm text-rose-400">
                (선택){' '}
                <a
                  href="http://review.riak.or.kr"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-rose-600">
                  <b>review.riak.or.kr</b>
                </a>{' '}
                회원이신 경우, 가입 당시 기입한 아이디/제작사명을 기입 해주세요.
              </div>
            </div>
          </div>

          {/* 구분선 */}
          <div className="flex items-center my-10">
            <div className="flex-grow border-t border-white/[0.08]" />
            <span className="px-3 text-sm font-semibold text-slate-500">
              곡 정보
            </span>
            <div className="flex-grow border-t border-white/[0.08]" />
          </div>

          {/* 곡 리스트 */}
          <div>
            <div className="text-sm text-slate-500 mb-3">
              <ul>
                <li className="mb-3">
                  저작권법에서는 저작물(음반)에 대한 저작자(작곡자, 작사자)와
                  실연자(가수, 연주자, 지휘자)의 성명을 표시하도록 하고
                  있습니다.
                  <br />
                  음반에 대한 저작자와 실연자의 성명 또는 예명을 기재하지 않을
                  경우 이들의 성명표시권 침해가 되어 법적 분쟁이 발생될 수
                  있으니 번거로우시더라도{' '}
                  <span className="text-cyan-600">
                    작곡자, 작사자, 편곡자, 피쳐링, 가수와
                    곡연주자(연주악기명)의 성명 또는 예명
                  </span>
                  을 필히 기재하여 주시기 바랍니다.
                </li>
                <li className="text-rose-400">
                  원음방송의 경우, 각 앨범당 최대 3곡만 심의를 받고 있습니다.{' '}
                  <span className="font-bold">
                    원음방송 심의를 위한 3곡 표시
                  </span>
                  를 해주세요.
                  <br /> (3곡 이하의 앨범은 표시하지 않으셔도 됩니다)
                </li>
              </ul>
            </div>

            <div className="border border-white/[0.08] rounded-2xl p-4 space-y-6 bg-[#182334]">
              {!songs.length && (
                <p className="text-sm text-slate-400">
                  앨범을 선택하면 곡 정보가 표시됩니다.
                </p>
              )}

              {songs.map((s) => (
                <div
                  key={s.id}
                  className="rounded-xl border border-white/[0.08] p-4 space-y-3 bg-[#182334]">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <ReadOnlyField label="타이틀YN" value={s.titleYn} />
                    <ReadOnlyField label="트랙" value={s.track} />
                    <ReadOnlyField label="곡명" value={s.nameKr} />
                    <ReadOnlyField label="작사" value={s.writerKr} />
                    <ReadOnlyField label="작곡" value={s.composerKr} />
                    <ReadOnlyField label="편곡" value={s.arrangerKr} />
                  </div>

                  {s.fileUrl && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                        음원 듣기
                      </label>
                      <audio
                        controls
                        src={s.fileUrl}
                        style={{ width: '100%' }}
                      />
                    </div>
                  )}

                  <InputField
                    label="피처링"
                    value={songInputs[s.id]?.featuring || ''}
                    onChange={(e) =>
                      updateSongInput(s.id, 'featuring', e.target.value)
                    }
                    placeholder="예) 홍길동"
                  />
                  <InputField
                    label="피처링 아티스트의 최근 음원"
                    value={songInputs[s.id]?.featuringLatestRelease || ''}
                    onChange={(e) =>
                      updateSongInput(
                        s.id,
                        'featuringLatestRelease',
                        e.target.value
                      )
                    }
                    placeholder="예) 홍길동 - 동해번쩍 서해번쩍"
                  />
                  <InputField
                    label="실연(연주자명/악기명)"
                    required
                    value={songInputs[s.id]?.performance || ''}
                    onChange={(e) =>
                      updateSongInput(s.id, 'performance', e.target.value)
                    }
                    placeholder="예) 홍길동/기타, 김철수/드럼"
                  />

                  {/* 가사 모달 버튼 (Inst. 곡은 제외) */}
                  {!(s.nameKr || '').includes('Inst.') && (
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
                        가사 <span className="text-red-500">*</span>
                      </label>
                      <ButtonItem
                        text="가사 입력"
                        color="lime"
                        onClick={() => openLyricsModal(s.id)}
                      />
                    </div>
                  )}

                  {/* 원음방송 체크박스 */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={songInputs[s.id]?.woneumYn || false}
                      onChange={(e) => updatewoneumYn(s.id, e.target.checked)}
                      className="peer hidden"
                    />
                    <span className="w-6 h-6 flex items-center justify-center border border-white/[0.08] rounded-full peer-checked:bg-sky-500 peer-checked:border-sky-500 text-white text-sm">
                      ✓
                    </span>
                    <span className="text-sm font-semibold text-slate-200">
                      원음방송 선택 (최대 3곡){' '}
                      <span className="text-red-500">*</span>
                    </span>
                  </label>
                </div>
              ))}
            </div>
          </div>

          {/* 제출 버튼 */}
          <div className="w-full flex justify-end items-center">
            <div className="mr-4 text-2xl font-bold text-sky-500">
              ₩110,000
            </div>
            <ButtonItem
              className="mr-2"
              text="결제하기"
              color="blue"
              onClick={onSubmit}
              disabled={!canSubmit}
            />
            <ButtonItem
              text="목록"
              color="gray"
              onClick={() => navigate(`/reviewPage`)}
            />
          </div>

          {/* 약관 */}
          <div className="text-sm text-slate-500 mb-3">
            <p>
              본 서비스는 심의 신청 접수 완료 후, 평균{' '}
              <span className="text-rose-400">2주 이내</span>에 방송사로
              전달되며, 각 방송사의 사정에 따라 심의 기간은 달라질 수 있습니다.
            </p>
            <p>
              신청 후 7일 이내로 게시판 접수를 통해{' '}
              <span className="text-rose-400">환불이 가능</span>하며, 신청 정보
              오류에 의한{' '}
              <span className="text-rose-400">수정/재접수는 별도 문의</span>{' '}
              바랍니다.
            </p>
            <p>
              검토가 완료되어 심의가 시작된 이후에는{' '}
              <span className="text-rose-400">환불이 불가</span>합니다.
            </p>
          </div>
      </div>

      {/* 가사 입력 모달 */}
      {lyricsModalOpen && (
        <div
          className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
          onClick={closeLyricsModal}>
          <div
            className="bg-[#182334] rounded-2xl p-6 w-3/4 h-3/4 flex flex-col shadow-xl border border-white/[0.08]"
            onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-white mb-2">
              가사 입력
            </h2>
            <div className="text-sm text-rose-400 mb-2">
              ※ 영문 가사의 경우 <b>의역이 필수</b>입니다. ex) I want you only
              (난 너만 있으면 돼)
            </div>
            <textarea
              value={tempLyrics}
              onChange={(e) => setTempLyrics(e.target.value)}
              className={`w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent resize-none ${hasTranslation ? 'flex-none h-2/5' : 'flex-1'}`}
              placeholder="곡의 전체 가사를 입력해 주세요."
            />
            <div className="mt-2 flex items-center gap-3 flex-wrap">
              <input type="checkbox" id="hasTranslationCheck" checked={hasTranslation} onChange={(e) => setHasTranslation(e.target.checked)} className="w-4 h-4 accent-sky-400" />
              <label htmlFor="hasTranslationCheck" className="text-sm text-slate-300 cursor-pointer">한글번역 포함</label>
              <button onClick={autoTranslate} disabled={translating || !tempLyrics.trim()}
                className="px-3 py-1 rounded-lg text-xs font-semibold bg-violet-500/20 text-violet-400 hover:bg-violet-500/30 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                {translating ? '번역 중...' : '✦ 자동번역'}
              </button>
            </div>
            {hasTranslation && (
              <textarea
                value={translating ? '번역 중...' : tempLyricsTranslation}
                onChange={(e) => setTempLyricsTranslation(e.target.value)}
                readOnly={translating}
                className="flex-1 w-full mt-2 px-3 py-2 rounded-xl border border-sky-500/30 bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 resize-none"
                placeholder="한글번역 가사를 입력해 주세요."
              />
            )}
            <div className="mt-4 flex justify-end gap-2">
              <ButtonItem text="취소" color="gray" onClick={closeLyricsModal} />
              <ButtonItem text="저장" color="blue" onClick={saveLyricsModal} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ReadOnlyField({ label, value, loading }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label}
      </label>
      <input
        type="text"
        value={loading ? '불러오는 중…' : value ?? ''}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-white/5 text-slate-400 text-sm focus:outline-none"
        readOnly
      />
    </div>
  );
}

function InputField({ label, value, onChange, required, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1.5">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        type="text"
        value={value}
        onChange={onChange}
        className="w-full px-3 py-2 rounded-xl border border-white/[0.08] bg-[#1a2b42] text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-sky-300 focus:border-transparent"
        placeholder={placeholder}
      />
    </div>
  );
}
